import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { SocietyReputationEntry } from '../types';

interface Props {
  reputation: SocietyReputationEntry[];
  /**
   * Society ids the viewer's active persona is currently wanted by (#1765 heat rows).
   * Own-view only — omit (or leave undefined) on foreign-view renders.
   */
  wantedSocietyIds?: Set<number>;
}

// Exported for reuse by the consolidated Reputation tab's Standing card (#1446),
// which renders the same named-tier badges for organization reputations.
export const TIER_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  reviled: 'destructive',
  despised: 'destructive',
  disliked: 'destructive',
  disfavored: 'secondary',
  unknown: 'outline',
  favored: 'secondary',
  liked: 'default',
  honored: 'default',
  revered: 'default',
};

export function formatTier(tier: string): string {
  return tier.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Per-society reputation list. Named tier labels only — the raw numeric
 * value is intentionally never exposed to the client (#676 spec).
 */
export function ReputationListCard({ reputation, wantedSocietyIds }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Reputation</CardTitle>
      </CardHeader>
      <CardContent>
        {reputation.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No societies have a recorded opinion of this persona yet.
          </p>
        ) : (
          <ul className="space-y-2 text-sm">
            {reputation.map((entry) => (
              <li key={entry.society_id} className="flex items-center justify-between">
                <span className="font-medium">{entry.society_name}</span>
                <div className="flex items-center gap-2">
                  {wantedSocietyIds?.has(entry.society_id) && (
                    <Badge variant="destructive">Wanted</Badge>
                  )}
                  <Badge variant={TIER_VARIANT[entry.tier] ?? 'outline'}>
                    {formatTier(entry.tier)}
                  </Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
